import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Play } from 'lucide-react';
import { TIMELINE, TIMELINE_OUTRO } from '@/constants/content';

export default function Timeline() {
  const containerRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = rect.height + vh * 0.5;
      const scrolled = Math.min(Math.max(vh - rect.top, 0), total);
      setProgress(Math.min(1, scrolled / total));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {/* Vertical hairline */}
      <div className="absolute left-8 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-taupe/25" aria-hidden />
      <div
        className="absolute left-8 md:left-1/2 md:-translate-x-1/2 top-0 w-px bg-gold origin-top transition-[height] duration-200"
        style={{ height: `${progress * 100}%` }}
        aria-hidden
      />

      <div className="space-y-20 md:space-y-28">
        {TIMELINE.map((step, idx) => {
          const isRight = idx % 2 === 1;
          const alignRight = !isRight; // left column entries render right-aligned on desktop
          return (
            <div
              key={`${step.year}-${step.title}`}
              className="relative md:grid md:grid-cols-2 md:gap-16 items-start"
              data-testid={`timeline-chapter-${idx}`}
            >
              {/* Dot */}
              <div
                className={`absolute left-8 md:left-1/2 -translate-x-1/2 mt-3 md:mt-4 w-3 h-3 rounded-full bg-ivory border ${
                  step.accent ? 'border-gold shadow-[0_0_0_6px_rgba(200,161,90,0.22)]' : 'border-gold shadow-[0_0_0_4px_rgba(200,161,90,0.15)]'
                }`}
                aria-hidden
              />

              <div
                className={`pl-20 md:pl-0 ${
                  isRight ? 'md:col-start-2 md:pl-16' : 'md:col-start-1 md:pr-16 md:text-right'
                }`}
              >
                {step.label && (
                  <div className={`font-label text-[10px] tracking-[0.28em] uppercase text-gold mb-3 ${alignRight ? 'md:justify-end' : ''}`}>
                    {step.label}
                  </div>
                )}

                <div
                  className={`font-serif-display italic text-taupe text-2xl md:text-3xl leading-none tracking-tighter mb-3 ${
                    alignRight ? 'md:text-right' : ''
                  }`}
                >
                  {step.year}
                </div>

                <div className={`eyebrow text-taupe mb-4 ${alignRight ? 'md:justify-end' : ''}`}>
                  {String(idx + 1).padStart(2, '0')} · {step.chapter || 'Chapter'}
                </div>

                <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl tracking-tighter text-matte leading-[1.02] mb-5">
                  {step.title}
                </h3>

                <p className={`text-matte/70 leading-[1.85] text-[15px] max-w-md ${alignRight ? 'md:ml-auto' : ''}`}>
                  {step.body}
                </p>

                {step.quote && (
                  <div className={`mt-8 max-w-md ${alignRight ? 'md:ml-auto' : ''}`}>
                    <div className={`flex items-start gap-4 ${alignRight ? 'md:flex-row-reverse md:text-right' : ''}`}>
                      <span className="mt-2 block w-10 h-px bg-gold flex-none" aria-hidden />
                      <p className="font-serif-display italic text-xl md:text-2xl text-matte leading-snug tracking-tight">
                        &ldquo;{step.quote}&rdquo;
                      </p>
                    </div>
                  </div>
                )}

                {step.ventures && (
                  <ul className={`mt-8 space-y-4 max-w-md ${alignRight ? 'md:ml-auto' : ''}`}>
                    {step.ventures.map((v) => (
                      <li key={v.name} className="border-t border-taupe/25 pt-4">
                        <div className={`font-label text-[10px] tracking-[0.28em] uppercase text-taupe mb-1 ${alignRight ? 'md:text-right' : ''}`}>
                          Venture
                        </div>
                        <div className={`font-serif-display text-lg md:text-xl text-matte tracking-tight ${alignRight ? 'md:text-right' : ''}`}>
                          {v.name}
                        </div>
                        <div className={`text-matte/70 text-[14px] leading-relaxed ${alignRight ? 'md:text-right' : ''}`}>
                          {v.desc}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {step.cta && (
                  <div className={`mt-8 ${alignRight ? 'md:text-right' : ''}`}>
                    <a
                      href={step.cta.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 font-label text-[11px] tracking-[0.28em] uppercase text-matte hover:text-gold border-b border-matte/30 hover:border-gold pb-1 transition-colors"
                      data-testid={`timeline-cta-${idx}`}
                    >
                      {step.cta.label}
                      <ArrowUpRight size={14} strokeWidth={1.6} />
                    </a>
                  </div>
                )}

                {step.milestone && (
                  <a
                    href={step.milestone.url}
                    target="_blank"
                    rel="noreferrer"
                    className={`group mt-10 block max-w-md border border-taupe/30 hover:border-gold bg-white/60 hover:bg-white transition-colors rounded-sm ${
                      alignRight ? 'md:ml-auto' : ''
                    }`}
                    data-testid={`timeline-milestone-${idx}`}
                  >
                    <div className="p-6">
                      <div className={`flex items-center gap-2 mb-4 ${alignRight ? 'md:flex-row-reverse md:justify-start' : ''}`}>
                        <span className="w-6 h-px bg-gold" aria-hidden />
                        <span className="font-label text-[10px] tracking-[0.28em] uppercase text-gold">
                          {step.milestone.kind}
                        </span>
                      </div>
                      <div className={`font-serif-display text-xl md:text-2xl text-matte leading-snug tracking-tight mb-3 ${alignRight ? 'md:text-right' : ''}`}>
                        {step.milestone.title}
                      </div>
                      {step.milestone.description && (
                        <p className={`text-matte/70 text-[14px] leading-relaxed mb-5 ${alignRight ? 'md:text-right' : ''}`}>
                          {step.milestone.description}
                        </p>
                      )}
                      <div className={`flex items-center gap-2 font-label text-[11px] tracking-[0.28em] uppercase text-matte group-hover:text-gold transition-colors ${
                        alignRight ? 'md:flex-row-reverse md:justify-start' : ''
                      }`}>
                        <Play size={12} strokeWidth={1.6} />
                        <span>{step.milestone.cta || 'Watch'}</span>
                        <ArrowUpRight size={12} strokeWidth={1.6} />
                      </div>
                    </div>
                  </a>
                )}
              </div>
            </div>
          );
        })}

        {/* Journey continues outro */}
        {TIMELINE_OUTRO && (
          <div className="relative md:grid md:grid-cols-2 md:gap-16 items-start" data-testid="timeline-outro">
            <div
              className="absolute left-8 md:left-1/2 -translate-x-1/2 mt-3 md:mt-4 w-3 h-3 rounded-full bg-gold shadow-[0_0_0_6px_rgba(200,161,90,0.22)]"
              aria-hidden
            />
            <div className="pl-20 md:pl-0 md:col-span-2 md:text-center md:max-w-2xl md:mx-auto">
              <div className="eyebrow text-gold mb-5 md:justify-center">{TIMELINE_OUTRO.eyebrow}</div>
              <p className="text-matte/75 leading-[1.9] text-[15px]">{TIMELINE_OUTRO.body}</p>
              <p className="mt-6 font-serif-display italic text-2xl md:text-3xl text-matte tracking-tight">
                {TIMELINE_OUTRO.closing}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
